import type {Vector3, Color} from 'three'
import {Mesh, ShaderMaterial, Vector2, Vector4, Group, DoubleSide} from 'three'

import assets from './assets'
import common from './common'
import shaders from './glsl/shaders'

import {CustomLineGeometry} from './custom-line-geometry'
import {commonUniforms} from './utils/common-uniforms'

import Timeline from './utils/timeline'
import type {Line} from './params/hero'

export default class Lines {
  lineParams: Line[]
  noAnimation: boolean
  group: Group
  lineWidthScale: number
  vertexNum: number
  uniforms: {[uniform: string]: {value: number | Vector2 | Vector4 | Vector3 | Color}}

  constructor(lineParams: Line[], lineWidthScale: number = 1, noAnimation: boolean = false) {
    this.lineParams = lineParams
    this.noAnimation = noAnimation
    this.group = new Group()
    this.lineWidthScale = lineWidthScale

    this.vertexNum = 200

    this.uniforms = {
      uSegmentNum: {
        value: 100,
      },
      uResolution: {
        value: common.fbo_screenSize,
      },
      ...commonUniforms,
    }
  }

  init() {
    for (const params of this.lineParams) {
      const assetData = assets.svgs[params.name]
      const _lineWidthScale = params.lineWidth || 1
      const paths = assetData?.paths
      const group = new Group()
      this.group.add(group)
      group.position.copy(params.position)
      group.rotation.copy(params.rotation)

      const roundGroups = new Group()
      group.add(roundGroups)

      const tl = new Timeline({
        easing: 'easeOutCubic',
        delay: common.delay.main,
      })

      if (paths) {
        for (let index = 0; index < paths.length; index++) {
          const shapePath = paths[index]

          if (!shapePath) continue
          const path = shapePath.currentPath || shapePath.subPaths[0]
          const lineIndex = index / (paths.length - 1)

          const geometry = new CustomLineGeometry(this.vertexNum, path, -1, lineIndex, false)
          const alphaMapVector = new Vector2(0, 0)
          const uniforms = {
            uProgress: {
              value: new Vector4(0, 0, 0, 0),
            },
          }

          const _alpha = params.alpha || 1
          const _blurRangeMin = 0.5

          const gradient = params.gradient[index] || new Vector4(0, 0.25, 0.75, 1)
          const material = this.createMaterial(
            params.scale,
            alphaMapVector,
            uniforms,
            _lineWidthScale,
            _alpha,
            _blurRangeMin,
            gradient,
          )
          // const timelineParams = nodeParams.timeline[index];

          if (this.noAnimation) {
            uniforms.uProgress.value.set(1, 1, 1, 1)
          } else {
            tl.to(
              [uniforms.uProgress.value],
              4000,
              {
                x: 1,
              },
              500,
            )
              .to(
                [uniforms.uProgress.value],
                4000,
                {
                  y: 1,
                },
                500 + 100,
              )
              .to(
                [uniforms.uProgress.value],
                3000,
                {
                  z: 1,
                },
                500 + 1000,
              )
          }

          const mesh = new Mesh(geometry, material)
          mesh.frustumCulled = false
          mesh.renderOrder = 0
          mesh.position.z = 0.00001 * index
          group.add(mesh)
        }
      }
      tl.start()
    }
  }

  createMaterial(
    scale: number,
    alphaMapVector: Vector2,
    uniforms: {[uniform: string]: {value: number | Vector2 | Vector4 | Vector3 | Color}},
    _lineWidthScale = 1,
    _alpha = 1,
    _blurRangeMin = 0.5,
    gradient: Vector4,
  ) {
    return new ShaderMaterial({
      vertexShader: shaders.lineVert,
      fragmentShader: shaders.utils + shaders.lineFrag,
      uniforms: {
        ...this.uniforms,
        ...uniforms,
        uScale: {
          value: scale,
        },
        uAlphaMapVector: {
          value: alphaMapVector,
        },
        uLineWidth: {
          value: this.lineWidthScale * 0.04 * _lineWidthScale,
        },
        uAlpha: {
          value: _alpha,
        },
        uBlurRangeMin: {
          value: _blurRangeMin,
        },
        uGradient: {
          value: gradient,
        },
      },
      side: DoubleSide,
      transparent: true,
    })
  }
}
